/* ==========================================================================
   Giorgio — an option that belongs to only some values of another option.

   The biscotti sells a ribbon on the Gift Box and on the Signature Jar, but
   never on the everyday Dozen. Hiding the ribbon selector is the easy half and
   Liquid already does it (see product-giorgio-variant-picker.liquid). This file
   handles the half that actually costs money: the CHOICE.

   Shopify has no concept of an option that only exists for some values of
   another one — every flavor × packaging × ribbon combination is a real
   variant. So a shopper who picks "Gift Box + Burnt Orange Ribbon" and then
   switches to "The Dozen" lands on "The Dozen + Burnt Orange Ribbon": a ribbon
   they can no longer see, cannot remove, and are still charged for.

   The fix has to land BEFORE the variant is computed, not after. Dawn's
   <variant-selects> listens for `change` in the bubble phase and then reads
   `fieldset input:checked` to decide which variant is selected. This listener
   sits on `document` in the CAPTURE phase, so it runs first: it flips the
   ribbon radio back to its neutral value while the event is still travelling
   down, and Dawn then reads the already-corrected state. One render, no flash
   of a wrong price, and no second fetch.

   Doing it here rather than in a re-render hook also means it survives Dawn
   replacing the section's innerHTML (inline <script> tags inserted that way
   never execute) and it works no matter how the option changed — a packaging
   card, the native radio, the browser's back button, or a shared URL.

   All of it is driven by data- attributes the theme editor writes, so no value
   name is hard-coded here; renaming "Gift Box" in Shopify is a settings edit.
   ========================================================================== */
(function () {
  'use strict';

  var HOST = 'variant-selects[data-gio-scope-option]';

  function config(host) {
    var when = (host.getAttribute('data-gio-scope-when') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    if (!when.length) return null;
    return {
      scoped: host.getAttribute('data-gio-scope-option'),
      source: host.getAttribute('data-gio-scope-source'),
      when: when,
      reset: (host.getAttribute('data-gio-scope-reset') || '').trim(),
    };
  }

  /* The fieldsets carry `product-form-variant-<position>` — the same class the
     picker uses to style each option row, so this needs no extra markup. */
  function fieldset(host, position) {
    return host.querySelector('.product-form-variant-' + position);
  }

  function checkedValue(scope) {
    var input = scope && scope.querySelector('input[type="radio"]:checked');
    return input ? input.value : null;
  }

  /* Returns true if it changed something. `pending` is the value the source
     option is ABOUT to take — during capture the radio being clicked is not
     checked yet, so its own value has to be passed in explicitly. */
  function reconcile(host, pending) {
    var cfg = config(host);
    if (!cfg || !cfg.reset) return false;

    var source = pending || checkedValue(fieldset(host, cfg.source));
    if (source === null) return false;
    if (cfg.when.indexOf(source) !== -1) return false;

    var scoped = fieldset(host, cfg.scoped);
    if (!scoped) return false;

    var current = scoped.querySelector('input[type="radio"]:checked');
    if (current && current.value === cfg.reset) return false;

    var neutral = Array.prototype.find.call(
      scoped.querySelectorAll('input[type="radio"]'),
      function (input) { return input.value === cfg.reset; }
    );
    if (!neutral) return false;

    neutral.checked = true;
    return true;
  }

  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || target.type !== 'radio') return;

      var host = target.closest ? target.closest(HOST) : null;
      if (!host) return;

      var cfg = config(host);
      if (!cfg) return;

      /* Only a change to the SOURCE option can invalidate the scoped one —
         reacting to the scoped option's own change would undo the shopper's
         pick the instant they made it. */
      var source = fieldset(host, cfg.source);
      if (!source || !source.contains(target)) return;

      reconcile(host, target.value);
    },
    true
  );

  /* A URL can arrive already in the impossible state — a link someone shared,
     or the back button. Correcting it needs a real `change`: nothing is
     in flight at load time for Dawn to pick the fix up from.

     The timing here is the whole trick. Deferred scripts run in DOCUMENT order
     and this one is declared before product-info.js, so at the moment it
     executes <product-info> has not been upgraded yet and its subscriber to
     the option-change event does not exist — a `change` fired now updates the
     radio and nothing else: right checkbox, wrong price, wrong variant in the
     form. Nor does the usual `readyState === 'loading'` guard help, because a
     deferred script runs at readyState 'interactive', which that test reads as
     "already loaded" and fires immediately.

     DOMContentLoaded is the first moment ALL deferred scripts have run, so
     that — not readyState — is what this waits for. Only a script injected
     after the page settled ('complete') can skip the wait. */
  function onLoad() {
    Array.prototype.forEach.call(document.querySelectorAll(HOST), function (host) {
      if (!reconcile(host, null)) return;
      var cfg = config(host);
      var scoped = fieldset(host, cfg.scoped);
      var neutral = scoped && scoped.querySelector('input[type="radio"]:checked');
      if (neutral) neutral.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  if (document.readyState === 'complete') {
    onLoad();
  } else {
    document.addEventListener('DOMContentLoaded', onLoad, { once: true });
  }
})();
